import { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { allFields, CERT_PARAGRAPHS } from "./sections";

const SPAN = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3 sm:col-span-2", 4: "lg:col-span-4 sm:col-span-2", 5: "lg:col-span-5 sm:col-span-2", 6: "lg:col-span-6 sm:col-span-2" };
const cell = "bg-white p-3 sm:p-3.5 focus-within:bg-[#FFFDF6] transition-colors";
const lbl = "block text-xs font-bold uppercase tracking-[0.06em] text-[#154F49] mb-1.5 leading-snug";
const inp = "w-full bg-transparent border-0 border-b border-[#154F49]/35 px-0.5 py-1.5 text-sm text-[#1A2E2B] placeholder:text-[#6B7D78] focus:outline-none focus:border-[#CDA34F] focus-visible:ring-0 aria-[invalid=true]:border-red-700";
const slug = (s) => s.replace(/\W+/g, "_");
const Req = () => <span className="text-[#9B742A]" aria-hidden> *</span>;

const Choices = ({ f, invalid }) => (
  <fieldset>
    <legend className={lbl}>{f.label}{f.required && <Req />}</legend>
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {f.options.map((o) => {
        const id = `app_${f.name}_${slug(o)}`;
        return (
          <label key={o} htmlFor={id} className="inline-flex items-center gap-2 text-sm text-[#1A2E2B] cursor-pointer">
            <input id={id} type={f.type} name={f.name} value={o} aria-invalid={invalid || undefined} data-testid={`field-${f.name}-${slug(o).toLowerCase()}`} className="w-4 h-4 accent-[#154F49] shrink-0" />
            {o}
          </label>
        );
      })}
    </div>
  </fieldset>
);

const formatSsn = (e) => {
  const d = e.target.value.replace(/\D/g, "").slice(0, 9);
  e.target.value = d.length > 5 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d;
};

const SecureInput = ({ f, common }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...common} type={show ? "text" : "password"} autoComplete="off" spellCheck={false} inputMode={f.type === "ssn" ? "numeric" : undefined} placeholder={f.type === "ssn" ? "###-##-####" : ""} onInput={f.type === "ssn" ? formatSsn : undefined} maxLength={f.type === "ssn" ? 11 : 40} className={`${inp} pr-9 tracking-wider`} />
      <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? `Hide ${f.label}` : `Show ${f.label}`} className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#154F49] no-print" data-testid={`toggle-${f.name}`}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const Cell = ({ f, error }) => {
  const id = `app_${f.name}`;
  const common = { id, name: f.name, required: f.required, placeholder: f.placeholder, autoComplete: f.autoComplete, "aria-invalid": error ? true : undefined, "aria-describedby": error ? `${id}_err` : f.hint ? `${id}_hint` : undefined, "data-testid": `field-${f.name}`, className: inp };
  let control;
  if (f.type === "checkbox" || f.type === "radio") control = <Choices f={f} invalid={!!error} />;
  else {
    const input = f.type === "textarea" ? <textarea rows={3} {...common} className={`${inp} resize-y`} />
      : f.type === "ssn" || f.type === "secure" ? <SecureInput f={f} common={common} />
      : <input type={f.type || "text"} {...common} />;
    control = (
      <>
        <label htmlFor={id} className={lbl}>{f.sensitive && <Lock className="inline w-3 h-3 mr-1 -mt-0.5 text-[#9B742A]" aria-hidden />}{f.label}{f.required && <Req />}</label>
        {input}
        {f.hint && <p id={`${id}_hint`} className="text-xs text-[#465F57] mt-1">{f.hint}</p>}
      </>
    );
  }
  return (
    <div className={`${cell} ${SPAN[f.span || 2]}`}>
      {control}
      {error && <p id={`${id}_err`} className="text-xs font-semibold text-red-800 mt-1">{error}</p>}
    </div>
  );
};

const Grid = ({ fields, errors }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-px bg-[#154F49]/30 border border-[#154F49]/30 rounded-xl overflow-hidden">
    {fields.map((f) => <Cell key={f.name} f={f} error={errors[f.name]} />)}
  </div>
);

const TableInput = ({ name, c, row }) => {
  const label = `${row} — ${c.label}`;
  if (c.type === "yn") return (
    <div className="flex gap-4" role="radiogroup" aria-label={label}>
      {["Yes", "No"].map((o) => <label key={o} className="inline-flex items-center gap-1.5 text-sm"><input type="radio" name={name} value={o} className="w-4 h-4 accent-[#154F49]" />{o}</label>)}
    </div>
  );
  if (c.type === "select") return (
    <select name={name} aria-label={label} className={inp} defaultValue="">
      <option value="">—</option>
      {c.options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
  return <input name={name} type={c.type || "text"} aria-label={label} className={inp} data-testid={`field-${name}`} />;
};

const COLS = { 2: "lg:grid-cols-2", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 7: "lg:grid-cols-7" };

const Table = ({ t }) => (
  <div className="border border-[#154F49]/30 rounded-xl overflow-hidden bg-[#154F49]/30 space-y-px">
    <div className={`hidden lg:grid ${COLS[t.cols.length]} bg-[#154F49] text-[#FBF8F1]`}>
      {t.cols.map((c) => <div key={c.key} className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider">{c.label}</div>)}
    </div>
    {t.rows.map((row, r) => (
      <div key={row} className="bg-white" data-testid={`table-row-${t.name}-${r}`}>
        <p className="lg:hidden px-3 pt-3 text-xs font-bold uppercase tracking-wider text-[#9B742A]">{row}</p>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${COLS[t.cols.length]} gap-px bg-[#154F49]/15`}>
          {t.cols.map((c, ci) => (
            <div key={c.key} className="bg-white p-3">
              <span className="lg:hidden block text-[11px] font-bold uppercase tracking-wide text-[#154F49] mb-1">{c.label}</span>
              {ci === 0 && <span className="hidden lg:block text-[11px] font-bold uppercase tracking-wide text-[#9B742A] mb-1">{row}</span>}
              <TableInput name={`${t.name}_${r}_${c.key}`} c={c} row={row} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export const Certification = ({ error }) => (
  <div className="space-y-4">
    <p className="text-sm font-bold uppercase tracking-wide text-[#154F49]">Please read carefully and sign that you understand and accept this information</p>
    <ol className="space-y-3 text-sm text-[#1A2E2B] leading-relaxed p-5 rounded-xl border border-[#154F49]/30 bg-white list-decimal pl-9">
      {CERT_PARAGRAPHS.map((p) => <li key={p.slice(0, 24)}>{p}</li>)}
    </ol>
    <label htmlFor="app_accept_terms" className="flex items-start gap-3 cursor-pointer">
      <input id="app_accept_terms" type="checkbox" name="accept_terms" value="Accepted" className="mt-0.5 w-5 h-5 accent-[#154F49] shrink-0" data-testid="field-accept_terms" aria-invalid={error ? true : undefined} />
      <span className="text-sm font-semibold text-[#154F49]">I have read, understand and accept the statements above.<span className="text-[#9B742A]"> *</span></span>
    </label>
  </div>
);

export const SectionBody = ({ s, errors }) => {
  const plain = s.fields || [];
  return (
    <div className="space-y-5">
      {s.certification && <Certification error={errors.accept_terms} />}
      {s.table && <Table t={s.table} />}
      {(s.groups || []).map((g) => (
        <div key={g.title} className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#9B742A]">{g.title}</p>
          <Grid fields={g.fields} errors={errors} />
        </div>
      ))}
      {plain.length > 0 && <Grid fields={plain} errors={errors} />}
    </div>
  );
};

export { allFields };
